import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import DefaultWebLayout from './layouts/DefaultWebLayout.jsx';
import DefaultWebLayoutAlt1 from './layouts/DefaultWebLayoutAlt1.jsx';
import DefaultWebLayoutAlt2 from './layouts/DefaultWebLayoutAlt2.jsx';
import AuthWebLayout from './layouts/AuthWebLayout.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import './App.scss';

// Generic status pages (create-frontend-layout-components) — 404 is wired
// below as the router catch-all.
const NotFound404 = lazy(() => import('./pages/status/NotFound404.jsx'));
const Unauthorized401 = lazy(() => import('./pages/status/Unauthorized401.jsx'));
const Forbidden403 = lazy(() => import('./pages/status/Forbidden403.jsx'));
const ServerError500 = lazy(() => import('./pages/status/ServerError500.jsx'));

// UC 2f1a9966 — Sign in to MarginOS and route to role landing page.
const LoginPage = lazy(() => import('./pages/login/index.jsx'));
const ActiveProjectDeliveryHub = lazy(() => import('./pages/project-delivery-operations/ActiveProjectDeliveryHub.jsx'));

// UC 1021f3de — Manage Active Project Delivery and Milestone Billing Proof Workspace.
const MilestoneBillingProofWorkspace = lazy(
  () => import('./pages/project-delivery-operations/MilestoneBillingProofWorkspace.jsx')
);

// UC f5210ad0 — Manage users, invitations, and RBAC assignments.
const UserAccessRbacConsole = lazy(() => import('./pages/access-identity-management/UserAccessRbacConsole.jsx'));

// UC 8fdad578 — Configure Tenant Rate Cards and System Policies.
const TenantSettingsRateCards = lazy(() => import('./pages/tenant-administration/TenantSettingsRateCards.jsx'));

// UC 9304372e — Manage Proposal Templates and Merged Scope Blocks.
const ProposalTemplateLibrary = lazy(() => import('./pages/proposal-governance/ProposalTemplateLibrary.jsx'));

// UC 82cfe8d7 — Configure and Authorize GHL CRM Connector.
const IntegrationConnectorsHub = lazy(() => import('./pages/integration-management/IntegrationConnectorsHub.jsx'));

// UC f378da6c — Ingest GHL CRM Commercial Context for Proposals.
const ProposalStudio = lazy(() => import('./pages/proposal-governance/ProposalStudio.jsx'));

// UC 46f9ec79 — Approve Proposal Scope and Discount Overrides.
const ProposalApprovalsQueue = lazy(() => import('./pages/proposal-governance/ProposalApprovalsQueue.jsx'));

// UC ae67ab89 — Track Proposal History and Compare Revisions.
const ProposalRevisionHistory = lazy(() => import('./pages/proposal-governance/ProposalRevisionHistory.jsx'));

// UC 467740a4 — Convert Proposal to PDF and Dispatch via PandaDoc or DocuSign.
const DocumentEsignHub = lazy(() => import('./pages/contract-skeleton/DocumentEsignHub.jsx'));

// UC d72ae263 — Manage Contract Templates and Clause Library.
const ContractTemplateLibrary = lazy(() => import('./pages/contract-skeleton/ContractTemplateLibrary.jsx'));

// UC 557a3bcc — Carry Proposal Terms into Contract Skeleton.
const ContractTermRepository = lazy(() => import('./pages/contract-skeleton/ContractTermRepository.jsx'));

// UC 067e4e5b — Validate Milestone Deliverable for Billing Trigger.
const MilestoneSignoffBillingWorkspace = lazy(
  () => import('./pages/billing-control/MilestoneSignoffBillingWorkspace.jsx')
);

// UC 2a654939 — Log and Audit T&M Billable Evidence Ledger.
const TMEvidenceExpenseLedger = lazy(
  () => import('./pages/project-delivery-operations/TMEvidenceExpenseLedger.jsx')
);

// Platform pages added alongside the DesignStudio catalog — operational
// surfaces for capabilities that had backend support but no interface.
const RenewalRadar = lazy(() => import('./pages/contract-skeleton/RenewalRadar.jsx'));
const RevenueAttribution = lazy(() => import('./pages/engagement-lifecycle-management/RevenueAttribution.jsx'));
const EinvoicingCompliance = lazy(() => import('./pages/billing-control/EinvoicingCompliance.jsx'));
const SchedulerConsole = lazy(() => import('./pages/tenant-administration/SchedulerConsole.jsx'));

// UC cf1e7912 — Sync GHL Billing Contacts for Invoice and Dunning Workflows.
const CollectionsDunningCommandCenter = lazy(
  () => import('./pages/collections-orchestration/CollectionsDunningCommandCenter.jsx')
);

// UC 55b51537 — Queue and Manually Trigger Overdue Client Billing Reminders.
const AccountManagerReminderQueue = lazy(
  () => import('./pages/project-delivery-operations/AccountManagerReminderQueue.jsx')
);

// UC 6bc74ce1 — Track Billing and Collections State by Engagement.
const EngagementCashflowHub = lazy(
  () => import('./pages/engagement-lifecycle-management/EngagementCashflowHub.jsx')
);

// UC a10bb353 — Review Payer Payment Behavior and Credit Profile.
const PayerProfileScorecard = lazy(
  () => import('./pages/payer-intelligence/PayerProfileScorecard.jsx')
);

// UC e1efc667 — Review OaaS Consumption Statement and Billing Run.
const OaasMeteringUsagePortal = lazy(() => import('./pages/oaas-metering/OaasMeteringUsagePortal.jsx'));

// Real pages + their layouts (_default-web-layout / -1ghj3iy / -1r2bgmj /
// auth-web-layout, all under ./layouts) are generated per the design
// reference by ui-components-from-reference, which also wires each page's
// route here and re-points its frontend/src/layouts/menuData.js entry off
// the 404 placeholder.

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Public — reached before authentication; must stay ahead of any
                ProtectedRoute/RoleRoute group so the redirect-to-login chain
                (see components/ProtectedRoute.jsx, components/RoleRoute.jsx)
                always has somewhere real to land. */}
            <Route element={<AuthWebLayout />}>
              <Route path="/login" element={<LoginPage />} />
            </Route>

            {/* Default route. "/" previously matched nothing and fell through to
                the catch-all 404, so the site root — and the navbar brand, which
                links here — landed on "page not found". Sign-in is the front
                door: LoginPage itself bounces an already-authenticated user to
                their role's landing page, so this is correct signed in or out. */}
            <Route path="/" element={<Navigate to="/login" replace />} />

            {/* Role-gated — Delivery / Project Lead's (and related roles')
                default landing page, per UC 2f1a9966's role -> landing map. */}
            <Route element={<DefaultWebLayoutAlt2 />}>
              <Route
                element={
                  <RoleRoute
                    roles={[
                      'Delivery / Project Lead',
                      'Revenue Owner',
                      'Account Manager',
                      'Xamun Delivery & Operations',
                    ]}
                  />
                }
              >
                <Route path="/active-project-delivery-hub" element={<ActiveProjectDeliveryHub />} />
              </Route>
            </Route>

            {/* Milestone Billing Proof Workspace — UC 1021f3de. Page permissions:
                viewable by Delivery / Project Lead, Finance / AR Operator, Account
                Manager; editable (submit proof) by Delivery / Project Lead,
                enforced server-side. Shares the hub's layout (design's raw layout
                name _default-web-layout-1r2bgmj). */}
            <Route element={<DefaultWebLayoutAlt2 />}>
              <Route
                element={
                  <RoleRoute roles={['Delivery / Project Lead', 'Finance / AR Operator', 'Account Manager']} />
                }
              >
                <Route path="/milestone-billing-proof-workspace" element={<MilestoneBillingProofWorkspace />} />
              </Route>
            </Route>

            {/* User Access & RBAC Console — UC f5210ad0. Page permissions: viewable/
                editable only by Xamun Delivery & Operations. Shares the same
                default-web-layout shell as DefaultWebLayoutAlt2 above. */}
            <Route element={<DefaultWebLayoutAlt2 />}>
              <Route element={<RoleRoute roles={['Xamun Delivery & Operations']} />}>
                <Route path="/user-access-rbac-console" element={<UserAccessRbacConsole />} />
              </Route>
            </Route>

            {/* Tenant Settings & Rate Cards — UC 8fdad578. Page permissions: viewable/
                editable only by Xamun Delivery & Operations. Layout matches the
                design's raw layout name (_default-web-layout). */}
            <Route element={<DefaultWebLayout />}>
              <Route element={<RoleRoute roles={['Xamun Delivery & Operations']} />}>
                <Route path="/tenant-settings-rate-cards" element={<TenantSettingsRateCards />} />
              </Route>
            </Route>

            {/* Proposal Template Library — UC 9304372e. Page permissions: viewable by
                Revenue Owner + Delivery / Project Lead; editable by Revenue Owner
                (enforced server-side on POST/PUT). Layout matches the design's raw
                layout name (_default-web-layout-1ghj3iy). */}
            <Route element={<DefaultWebLayoutAlt1 />}>
              <Route element={<RoleRoute roles={['Revenue Owner', 'Delivery / Project Lead']} />}>
                <Route path="/proposal-template-library" element={<ProposalTemplateLibrary />} />
              </Route>
            </Route>

            {/* Integration Connectors Hub — UC 82cfe8d7. Page permissions: viewable/
                editable only by Xamun Delivery & Operations. Layout matches the
                design's raw layout name (_default-web-layout). */}
            <Route element={<DefaultWebLayout />}>
              <Route element={<RoleRoute roles={['Xamun Delivery & Operations']} />}>
                <Route path="/integration-connectors-hub" element={<IntegrationConnectorsHub />} />
              </Route>
            </Route>

            {/* Proposal Studio Workspace — UC f378da6c. Page permissions: viewable by
                Revenue Owner + Delivery / Project Lead; editable by Revenue Owner
                (enforced server-side on the GHL ingestion endpoints). Layout matches
                the design's raw layout name (_default-web-layout). Default (entry)
                page per scope. */}
            <Route element={<DefaultWebLayout />}>
              <Route element={<RoleRoute roles={['Revenue Owner', 'Delivery / Project Lead']} />}>
                <Route path="/proposal-studio" element={<ProposalStudio />} />
              </Route>
            </Route>

            {/* Proposal Approvals Queue — UC 46f9ec79. Page permissions: viewable/
                decidable only by Revenue Owner — the use case's sole actor role
                (per scope: both the use case's userRole and its user story's
                acceptance criteria name Revenue Owner as the approver, same role
                that requests an override from Proposal Studio). Layout matches
                the design's raw layout name (_default-web-layout). */}
            <Route element={<DefaultWebLayout />}>
              <Route element={<RoleRoute roles={['Revenue Owner']} />}>
                <Route path="/proposal-approvals-queue" element={<ProposalApprovalsQueue />} />
              </Route>
            </Route>

            {/* Proposal Revision History — UC ae67ab89. Page permissions: viewable by
                Revenue Owner, Delivery / Project Lead, Finance / AR Operator; restoring a
                revision back into the working draft is scoped server-side (the endpoint it
                reuses, POST /api/proposal-approvals, is already Revenue-Owner-only) and hidden
                client-side for the other viewer roles. Layout matches the design's raw layout
                name (_default-web-layout-1ghj3iy). */}
            <Route element={<DefaultWebLayoutAlt1 />}>
              <Route
                element={<RoleRoute roles={['Revenue Owner', 'Delivery / Project Lead', 'Finance / AR Operator']} />}
              >
                <Route path="/proposal-history-versioning" element={<ProposalRevisionHistory />} />
              </Route>
            </Route>

            {/* Document & E-Sign Hub — UC 467740a4. Page permissions: viewable by
                Revenue Owner, Finance / AR Operator, Delivery / Project Lead;
                editable (convert/dispatch/resend) by Revenue Owner + Finance /
                AR Operator (enforced server-side). Layout matches the design's
                raw layout name (_default-web-layout-1ghj3iy). */}
            <Route element={<DefaultWebLayoutAlt1 />}>
              <Route
                element={<RoleRoute roles={['Revenue Owner', 'Finance / AR Operator', 'Delivery / Project Lead']} />}
              >
                <Route path="/document-esign-hub" element={<DocumentEsignHub />} />
              </Route>
            </Route>

            {/* Contract Template Library — UC d72ae263. Page permissions: viewable/
                editable by Revenue Owner + Finance / AR Operator (enforced server-
                side on POST/PUT/upload). Layout matches the design's raw layout name
                (_default-web-layout-1ghj3iy). */}
            <Route element={<DefaultWebLayoutAlt1 />}>
              <Route element={<RoleRoute roles={['Revenue Owner', 'Finance / AR Operator']} />}>
                <Route path="/contract-template-library" element={<ContractTemplateLibrary />} />
              </Route>
            </Route>

            {/* Contract Term Repository — UC 557a3bcc. Page permissions: viewable by
                Revenue Owner + Finance / AR Operator (the transfer itself is gated
                server-side to Revenue Owner — the use case's own actor role). Layout
                matches the design's raw layout name (_default-web-layout). */}
            <Route element={<DefaultWebLayout />}>
              <Route element={<RoleRoute roles={['Revenue Owner', 'Finance / AR Operator']} />}>
                <Route path="/contract-term-repository" element={<ContractTermRepository />} />
              </Route>
            </Route>

            {/* Renewal Radar. Renewal is a commercial conversation, so it is
                scoped to the same roles that own contracts. */}
            <Route element={<DefaultWebLayout />}>
              <Route element={<RoleRoute roles={['Revenue Owner', 'Finance / AR Operator']} />}>
                <Route path="/renewal-radar" element={<RenewalRadar />} />
              </Route>
            </Route>

            {/* Revenue Attribution — campaign to collected cash. */}
            <Route element={<DefaultWebLayout />}>
              <Route element={<RoleRoute roles={['Revenue Owner', 'Finance / AR Operator']} />}>
                <Route path="/revenue-attribution" element={<RevenueAttribution />} />
              </Route>
            </Route>

            {/* UAE E-Invoicing Compliance. Finance operates it; Ops Admin
                configures the tenant tax identity behind it. */}
            <Route element={<DefaultWebLayout />}>
              <Route
                element={<RoleRoute roles={['Finance / AR Operator', 'Revenue Owner', 'Xamun Delivery & Operations']} />}
              >
                <Route path="/einvoicing-compliance" element={<EinvoicingCompliance />} />
              </Route>
            </Route>

            {/* Scheduler Console — tenant-level operational configuration,
                same standing as every other tenant policy surface. */}
            <Route element={<DefaultWebLayout />}>
              <Route
                element={<RoleRoute roles={['Xamun Delivery & Operations', 'Finance / AR Operator', 'Revenue Owner']} />}
              >
                <Route path="/scheduler-console" element={<SchedulerConsole />} />
              </Route>
            </Route>

            {/* Milestone Signoff & Billing Workspace — UC 067e4e5b. Page permissions
                (per scope's user_role edges): viewable by Delivery / Project Lead +
                Finance / AR Operator. This activity implements the Delivery Lead
                Signoff Queue tab only — the Finance Invoicing & Ledger Sync tab's
                real invoice/ERP-sync logic belongs to UC 1331d84b (not yet built)
                and is a UI-only placeholder here. Layout matches the design's raw
                layout name (_default-web-layout). */}
            <Route element={<DefaultWebLayout />}>
              <Route element={<RoleRoute roles={['Delivery / Project Lead', 'Finance / AR Operator']} />}>
                <Route path="/milestone-signoff-billing-workspace" element={<MilestoneSignoffBillingWorkspace />} />
              </Route>
            </Route>

            {/* T&M Evidence & Expense Ledger — UC 2a654939. Page permissions: viewable
                by Delivery / Project Lead, Finance / AR Operator, Revenue Owner; editable
                (log item, delete draft, approve batch) by Delivery / Project Lead + Finance
                / AR Operator, enforced server-side. Shares the hub's layout (design's raw
                layout name _default-web-layout-1r2bgmj). */}
            <Route element={<DefaultWebLayoutAlt2 />}>
              <Route
                element={<RoleRoute roles={['Delivery / Project Lead', 'Finance / AR Operator', 'Revenue Owner']} />}
              >
                <Route path="/t-m-evidence-expense-ledger" element={<TMEvidenceExpenseLedger />} />
              </Route>
            </Route>

            {/* Collections & Dunning Command Center — UC cf1e7912 (shared with UC
                6fc9e70a, not yet built). Page permissions: viewable/editable only
                by Finance / AR Operator (the use case's own actor role, enforced
                server-side). Layout matches the design's raw layout name
                (_default-web-layout). */}
            <Route element={<DefaultWebLayout />}>
              <Route element={<RoleRoute roles={['Finance / AR Operator']} />}>
                <Route path="/collections-dunning-command-center" element={<CollectionsDunningCommandCenter />} />
              </Route>
            </Route>

            {/* Account Manager Reminder Queue — UC 55b51537. Page permissions (per
                scope's page role edges): viewable by Revenue Owner, Account Manager,
                Finance / AR Operator; manually dispatching a reminder is scoped
                server-side to Account Manager (the use case's own actor) + Finance /
                AR Operator. Shares the hub's layout (design's raw layout name
                _default-web-layout-1r2bgmj). Self-transitions to itself on "Send
                Manual Reminder via GHL" per the use case's workflow. */}
            <Route element={<DefaultWebLayoutAlt2 />}>
              <Route
                element={<RoleRoute roles={['Revenue Owner', 'Account Manager', 'Finance / AR Operator']} />}
              >
                <Route path="/account-manager-reminder-queue" element={<AccountManagerReminderQueue />} />
              </Route>
            </Route>

            {/* Engagement Lifecycle Tracker & Cashflow Hub — UC 6bc74ce1. Page
                permissions: viewable/editable by Revenue Owner, Finance / AR
                Operator, Delivery / Project Lead (per scope's page description).
                Layout matches the design's raw layout name (_default-web-layout-1ghj3iy). */}
            <Route element={<DefaultWebLayoutAlt1 />}>
              <Route
                element={<RoleRoute roles={['Revenue Owner', 'Finance / AR Operator', 'Delivery / Project Lead']} />}
              >
                <Route path="/engagement-lifecycle-tracker-cashflow-hub" element={<EngagementCashflowHub />} />
              </Route>
            </Route>

            {/* Payer Profile & Scorecard — UC a10bb353. Page permissions: viewable
                by Revenue Owner (the use case's own actor) + Finance / AR Operator
                (owns the underlying invoices/disputes this scorecard reads).
                Read-only page. Layout matches the design's raw layout name
                (_default-web-layout). */}
            <Route element={<DefaultWebLayout />}>
              <Route element={<RoleRoute roles={['Revenue Owner', 'Finance / AR Operator']} />}>
                <Route path="/payer-profile-scorecard" element={<PayerProfileScorecard />} />
              </Route>
            </Route>

            {/* OaaS Metering & Usage Portal — UC e1efc667. Page permissions (per
                scope's page role edges): viewable by Finance / AR Operator
                (the use case's own actor) + Xamun Delivery & Operations
                (tenant ops oversight); running the billing batch and
                approving a statement are scoped server-side to Finance / AR
                Operator only. Layout matches the design's raw layout name
                (_default-web-layout). */}
            <Route element={<DefaultWebLayout />}>
              <Route element={<RoleRoute roles={['Finance / AR Operator', 'Xamun Delivery & Operations']} />}>
                <Route path="/oaas-metering-usage-portal" element={<OaasMeteringUsagePortal />} />
              </Route>
            </Route>

            {/* Page routes are added here per layout as they're built. */}
            <Route element={<DefaultWebLayout />}>
              <Route path="/401" element={<Unauthorized401 />} />
              <Route path="/403" element={<Forbidden403 />} />
              <Route path="/500" element={<ServerError500 />} />
              <Route path="*" element={<NotFound404 />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
